import React, { Component } from "react";
import PropTypes from "prop-types";
import { GenericService } from "@services";
import utils from "@utils";
import { INVOICE_FEE_TYPES } from "@constants";
import classNames from "classnames";
import "../EditInvoiceModal.scss";

export class EditableItem extends Component {
	constructor(props) {
		super(props);

		this.initialState = {
			vehicleTypes: [],
			ItemTypeId: props.item.ItemTypeId,
			ItemName: props.item.ItemName,
			Price: props.item.Price,
			Quantity: props.item.Quantity,
			ServiceChargeTypeId: props.item.ServiceChargeTypeId,
			_id: props.item._id,
			_validation: {
				ItemName: "",
				Price: "",
				Quantity: "",
				ServiceChargeTypeId: "",
			},
		};

		this.state = { ...this.initialState };
	}

	componentDidMount = () => this.getVehicleTypes();

	/** Retrieves a list of selectable vehicle types */
	getVehicleTypes = () => {
		const { lang } = global;
		GenericService.getVehicleTypes()
			.then(({ data }) => {
				const vehicleTypes = data.map(({ Id: value, Name: label }) => ({ value, label }));
				this.setState({ vehicleTypes });
			})
			.catch(() => {
				utils.notify({
					type: "error",
					message: lang.t("Error loading Vehicle Types"),
				});
			});
	};

	/**
	 * Handles all changes to `<select>` and `<input>` values
	 * @param {Event} event The HTML event handled by this method
	 */
	handleChange = ({ target: { type, name, value: rawValue } }) => {
		let value = rawValue;
		if (type === "tel") {
			const numericValue = rawValue.replace(/[^0-9.]/gi, "");
			value = numericValue;
		} else if (type === "select-one") {
			const { vehicleTypes } = this.state;
			const ItemName = vehicleTypes.find(
				({ value: val }) => val === parseInt(value, 10),
			)?.label;
			this.setState({ ItemName });
		}

		this.setState({ [name]: value });
	};

	onCancel = () => {
		const { onCancel } = this.props;

		this.setState(this.initialState);
		onCancel();
	};

	/** Validate that acceptable values have been entered for each input prior to saving an invoice item */
	validate = () => {
		const {
			onSave,
			item: { Id = null },
		} = this.props;
		const { ItemName, Price, Quantity, ItemTypeId, ServiceChargeTypeId, _id } = this.state;

		const _validation = {};

		_validation.ItemName = ItemName !== "" ? "" : "has-error";
		_validation.Price = Price >= 0 ? "" : "has-error";
		_validation.Quantity = Quantity > 0 ? "" : "has-error";

		if (ItemTypeId === INVOICE_FEE_TYPES.JURISDICTION_VEHICLE.value) {
			_validation.ServiceChargeTypeId = ServiceChargeTypeId ? "" : "has-error";
		}

		if (Object.values(_validation).includes("has-error")) {
			this.setState({ _validation });
		} else {
			const sentItem = {
				ItemTypeId,
				ItemName,
				Price,
				Quantity,
				ServiceChargeTypeId,
			};

			if (Id) sentItem.Id = Id;
			else sentItem._id = _id;

			onSave(sentItem);
		}
	};

	render() {
		const { lang } = global;
		const {
			ItemTypeId,
			ItemName,
			Price,
			Quantity,
			ServiceChargeTypeId,
			_validation,
			vehicleTypes,
		} = this.state;
		return (
			<div className="row form-group invoice-item">
				<Choose>
					<When condition={ItemTypeId === INVOICE_FEE_TYPES.JURISDICTION_VEHICLE.value}>
						<div className={classNames("col-sm-3", _validation.ServiceChargeTypeId)}>
							<label
								htmlFor="vehicle-type-select"
								className="control-label visible-xs"
							>
								<small>{lang.t("Vehicle Types")}</small>
							</label>
							<select
								id="vehicle-type-select"
								className="form-control"
								name="ServiceChargeTypeId"
								value={ServiceChargeTypeId}
								onChange={this.handleChange}
								onKeyPress={({ key }) => {
									if (key === "Enter") this.validate();
								}}
							>
								<option value="">{lang.t("Select a vehicle type")}</option>
								<For
									each="option"
									of={vehicleTypes}
								>
									<option
										key={`vehicle-${option.value}`}
										value={option.value}
									>
										{option.label}
									</option>
								</For>
							</select>
						</div>
					</When>
					<Otherwise>
						<div className={classNames("col-sm-3", _validation.ItemName)}>
							<label
								htmlFor="ItemName"
								className="control-label visible-xs"
							>
								<small>{lang.t("Item Name")}</small>
							</label>
							<input
								className="form-control"
								name="ItemName"
								id="ItemName"
								value={ItemName}
								onChange={this.handleChange}
								onKeyPress={({ key }) => {
									if (key === "Enter") this.validate();
								}}
								disabled={ItemTypeId === INVOICE_FEE_TYPES.TIMECARD.value}
							/>
						</div>
					</Otherwise>
				</Choose>
				<div className={classNames("col-xs-4 col-sm-2 spacing-top", _validation.Price)}>
					<label
						htmlFor="Price"
						className="control-label visible-xs"
					>
						<small>{lang.t("Rate")}</small>
					</label>
					<input
						className="form-control"
						name="Price"
						id="Price"
						type="tel"
						value={Price}
						onChange={this.handleChange}
						onKeyPress={({ key }) => {
							if (key === "Enter") this.validate();
						}}
					/>
				</div>
				<div className={classNames("col-xs-4 col-sm-2 spacing-top", _validation.Quantity)}>
					<label
						htmlFor="Quantity"
						className="control-label visible-xs"
					>
						<small>{lang.t("Quantity")}</small>
					</label>
					<input
						className="form-control"
						name="Quantity"
						id="Quantity"
						type="tel"
						value={Quantity}
						onChange={this.handleChange}
						onKeyPress={({ key }) => {
							if (key === "Enter") this.validate();
						}}
					/>
				</div>
				<div className="col-xs-4 col-sm-1 spacing-top">
					<small className="visible-xs">{lang.t("Total")}</small>
					<br className="visible-xs" />
					{utils.currencyFormatter(parseFloat(Price) * parseFloat(Quantity))}
				</div>
				<div className="col-xs-6 col-sm-2 spacing-top">
					<button
						type="button"
						className="btn btn-primary btn-block"
						onClick={this.validate}
					>
						{lang.t("Save")}
					</button>
				</div>
				<div className="col-xs-6 col-sm-2 spacing-top">
					<button
						type="button"
						className="btn btn-danger btn-block"
						onClick={this.onCancel}
					>
						{lang.t("Cancel")}
					</button>
				</div>
			</div>
		);
	}
}

EditableItem.propTypes = {
	item: PropTypes.object.isRequired,
	onCancel: PropTypes.func.isRequired,
	onSave: PropTypes.func.isRequired,
};

export default EditableItem;
